
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

const PatientLogin = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { signIn } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await signIn(email, password);
      if (error) {
        toast({
          title: "Login Failed",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Welcome back!",
          description: "Redirecting to your dashboard...",
        });
        navigate('/app/patient/dashboard');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-juv-cream-white to-juv-soft-blush">
      <Card className="w-full max-w-md bg-white/95 backdrop-blur-sm shadow-lg border border-juv-rose-clay/20">
        <CardHeader className="text-center space-y-4">
          <div className="flex items-center justify-center mb-4">
            <Link 
              to="/login"
              className="absolute left-6 p-2 rounded-lg hover:bg-juv-soft-blush/30 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-juv-deep-chestnut" />
            </Link>
            <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center bg-gradient-to-br from-juv-terracotta to-juv-deep-chestnut">
              <span className="text-white text-2xl font-bold font-juv-heading">J</span>
            </div>
          </div>
          <div>
            <CardTitle className="text-2xl font-bold font-juv-heading text-juv-deep-chestnut">
              Welcome to JUV
            </CardTitle>
            <p className="text-juv-deep-chestnut/70 mt-2">
              Your self-care journey starts here.
            </p>
            <p className="text-sm text-juv-deep-chestnut/60 mt-1">
              Book treatments, manage your Studio Credits, and unlock exclusive member rewards.
            </p>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-juv-deep-chestnut font-medium">Email</Label>
              <div className="relative">
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="border-juv-rose-clay/30 bg-white focus:border-juv-terracotta focus:ring-juv-terracotta/20 pl-10"
                  required
                />
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-juv-deep-chestnut/50" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-juv-deep-chestnut font-medium">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="border-juv-rose-clay/30 bg-white focus:border-juv-terracotta focus:ring-juv-terracotta/20 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:opacity-70 text-juv-deep-chestnut/50"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="w-full text-white hover:opacity-90 transition-all duration-300 bg-gradient-to-r from-juv-terracotta to-juv-deep-chestnut"
            >
              {loading ? 'Signing in...' : 'Log In as Patient'}
            </Button>
          </form>

          <div className="text-center">
            <Link 
              to="/auth" 
              className="text-sm hover:opacity-70 transition-colors text-juv-terracotta"
            >
              Forgot your password?
            </Link>
          </div>

          <div className="text-center text-sm text-juv-deep-chestnut/70">
            Don't have an account? 
            <Link 
              to="/auth"
              className="ml-1 font-medium transition-colors hover:opacity-70 text-juv-terracotta"
            >
              Join JUV
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PatientLogin;
